import React, { createContext, useContext, useMemo } from 'react';
import { useLeftPanelState } from '@/hooks/ui/useLeftPanelState';

interface ChatHistoryContextType {
	isOpen: boolean;
	open: () => void;
	close: () => void;
	toggle: () => void;
}

const ChatHistoryContext = createContext<ChatHistoryContextType | undefined>(undefined);

/**
 * Provides chat history drawer open/close state at the layout level.
 * Kept separate from AssistantContext so the sidebar (outside AssistantProvider) can trigger it,
 * while AssistantContainer (inside AssistantProvider) renders the actual drawer with full context access.
 */
export const ChatHistoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { isOpen, open, close, toggle } = useLeftPanelState();
	// Stable value so consumers only rerender when isOpen changes, not on every parent rerender
	// (open/close/toggle are useCallback-stable).
	const value = useMemo(() => ({ isOpen, open, close, toggle }), [isOpen, open, close, toggle]);
	return <ChatHistoryContext.Provider value={value}>{children}</ChatHistoryContext.Provider>;
};

export const useChatHistory = (): ChatHistoryContextType => {
	const context = useContext(ChatHistoryContext);
	if (!context) throw new Error('useChatHistory must be used within ChatHistoryProvider');
	return context;
};
